import { Constants, PropertyConstants } from '../../../Models/Constants';
import { IMultiSectionUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { IRangeValidation } from '../../../Models/IRangeValidation';
import { getPreferredUnits } from '../../../Services/Global';
import { ProductDesignFormState } from './ProductDesignFormTypes';

import { formatUnitValue, formatTemperatureDelta, roundedDisplayDelta, UnitType } from '../../../Utils/UnitsHelper';

/**
 * Utility function to create error messages for validation
 */
export function setErrorMessage(lessThan: number, greaterThan: number, unitType: UnitType | null = 'temperature'): string {
  return `The value is not within ${formatUnitValue(lessThan, unitType, false)} and ${formatUnitValue(greaterThan, unitType, false)}.`;
}

/**
 * Helper function to check if a value has been entered by the user
 * Returns true if the value is a valid number (not undefined, null, empty string, or NaN)
 */
function hasValue(value: any): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return !isNaN(numValue) && isFinite(numValue);
}

/**
 * Function to validate temperature relationships and return all validation errors
 * Now returns an array of error messages instead of overwriting them
 * Only validates if values have been entered by the user
 */
export function validateTemperature(
  inputValues: { [key: string]: any },
  rangeValidations: IRangeValidation[] | undefined,
  unitConfiguration: IMultiSectionUnitConfiguration | undefined
): string[] {
  let errorMessages: string[] = [];
  
  // Get values and check if they have been entered
  const hotMediaTempIn = inputValues[Constants.HotMediaTempIn];
  const hotMediaTempOut = inputValues[Constants.HotMediaTempOut];
  const coldMediaTempIn = inputValues[Constants.ColdMediaTempIn];
  const coldMediaTempOut = inputValues[Constants.ColdMediaTempOut];
  
  // Check if required values are present before performing calculations
  const hasHotMediaTempIn = hasValue(hotMediaTempIn);
  const hasHotMediaTempOut = hasValue(hotMediaTempOut);
  const hasColdMediaTempIn = hasValue(coldMediaTempIn);
  const hasColdMediaTempOut = hasValue(coldMediaTempOut);
  
  // Validate that Hot Side Temp In is not equal to Cold Side Temp Out
  if (hasHotMediaTempIn && hasColdMediaTempOut) {
    if (parseFloat(hotMediaTempIn) === parseFloat(coldMediaTempOut)) {
      errorMessages.push(`Temperature error: ${unitConfiguration?.hotMediaType} inlet temperature cannot be equal to ${unitConfiguration?.coldMediaType} outlet temperature.`);
    }
  }

  // Validate that Cold Side Temp In is not equal to Hot Side Temp Out
  if (hasColdMediaTempIn && hasHotMediaTempOut) {
    if (parseFloat(coldMediaTempIn) === parseFloat(hotMediaTempOut)) {
      errorMessages.push(`Temperature error: ${unitConfiguration?.coldMediaType} inlet temperature cannot be equal to ${unitConfiguration?.hotMediaType} outlet temperature.`);
    }
  }

  // Validate that Hot Side Temp In is not equal to Hot Side Temp Out (except for Steam/condensing media)
  if (hasHotMediaTempIn && hasHotMediaTempOut && unitConfiguration?.hotMediaType?.toLowerCase() !== 'steam') {
    if (parseFloat(hotMediaTempIn) === parseFloat(hotMediaTempOut)) {
      errorMessages.push(`Temperature error: ${unitConfiguration?.hotMediaType} inlet temperature cannot be equal to outlet temperature.`);
    }
  }

  // Validate that Cold Side Temp In is not equal to Cold Side Temp Out (except for Steam/condensing media)
  if (hasColdMediaTempIn && hasColdMediaTempOut && unitConfiguration?.coldMediaType?.toLowerCase() !== 'steam') {
    if (parseFloat(coldMediaTempIn) === parseFloat(coldMediaTempOut)) {
      errorMessages.push(`Temperature error: ${unitConfiguration?.coldMediaType} inlet temperature cannot be equal to outlet temperature.`);
    }
  }
  
  // Only calculate differences if both required values are present
  const TempApproachHotInletCurr = (hasHotMediaTempIn && hasColdMediaTempOut) 
    ? parseFloat(hotMediaTempIn) - parseFloat(coldMediaTempOut) 
    : NaN;
  const TempApproachHotOutletCurr = (hasHotMediaTempOut && hasColdMediaTempIn) 
    ? parseFloat(hotMediaTempOut) - parseFloat(coldMediaTempIn) 
    : NaN;
  const DeltaTemperatureColdSideCurr = (hasColdMediaTempOut && hasColdMediaTempIn) 
    ? parseFloat(coldMediaTempOut) - parseFloat(coldMediaTempIn) 
    : NaN;
  const DeltaTemperatureHotSideCurr = (hasHotMediaTempIn && hasHotMediaTempOut) 
    ? parseFloat(hotMediaTempIn) - parseFloat(hotMediaTempOut) 
    : NaN;

  const TempApproachHotInlet = rangeValidations?.find(x => x.propertyName === PropertyConstants.TempApproachHotInlet);
  const TempApproachHotOutlet = rangeValidations?.find(x => x.propertyName === PropertyConstants.TempApproachHotOutlet);
  const DeltaTemperatureColdSide = rangeValidations?.find(x => x.propertyName === PropertyConstants.DeltaTemperatureColdSide);
  const DeltaTemperatureHotSide = rangeValidations?.find(x => x.propertyName === PropertyConstants.DeltaTemperatureHotSide);

  // Helper to format absolute temperature values
  const fmtTemp = (val: number | undefined) => val !== undefined && !isNaN(val) ? formatUnitValue(val, 'temperature', false) : '';
  // Helper to format temperature *differences* (approach / delta): scales by factor only, no +32 offset
  const fmtDelta = (val: number | undefined) => val !== undefined && !isNaN(val) ? formatTemperatureDelta(val, false) : '';

  // Check TempApproachHotInlet validation
  // Validate only if at least one constraint is defined
  if (TempApproachHotInlet && !Number.isNaN(TempApproachHotInletCurr)) {
    const hasLessThan = TempApproachHotInlet.lessThan != null && TempApproachHotInlet.lessThan !== 0;
    const hasGreaterThan = TempApproachHotInlet.greaterThan != null && TempApproachHotInlet.greaterThan !== 0;
    const violatesLessThan = hasLessThan && roundedDisplayDelta(TempApproachHotInletCurr) < roundedDisplayDelta(TempApproachHotInlet.lessThan!);
    const violatesGreaterThan = hasGreaterThan && roundedDisplayDelta(TempApproachHotInletCurr) > roundedDisplayDelta(TempApproachHotInlet.greaterThan!);
    
    if (violatesLessThan || violatesGreaterThan) {
      // Create a more descriptive error message based on the actual validation failure
      let rangeDescription = '';
      let suggestedRange = '';
      let fieldRangeInfo = '';
      
      // Get the individual field validation range for ColdMediaTempOut
      const coldMediaTempOutRange = rangeValidations?.find(x => x.propertyName === Constants.ColdMediaTempOut);
      
      if (violatesLessThan) {
        rangeDescription = `too small (${fmtDelta(TempApproachHotInletCurr)}) - must be at least ${fmtDelta(TempApproachHotInlet.lessThan)}`;
      } else if (violatesGreaterThan) {
        rangeDescription = `too large (${fmtDelta(TempApproachHotInletCurr)}) - must be at most ${fmtDelta(TempApproachHotInlet.greaterThan)}`;
      }
      
      // Calculate the suggested range for cold media outlet temperature
      // Use the already extracted value
      
      // greaterThan determines MINIMUM difference → gives UPPER BOUND for ColdMediaTempOut
      // Condition: HotMediaTempIn - ColdMediaTempOut ≥ greaterThan
      // Which means: ColdMediaTempOut ≤ HotMediaTempIn - greaterThan
      let upperBoundFromTempApproach: number | undefined = undefined;
      if (hasGreaterThan) {
        upperBoundFromTempApproach = parseFloat(hotMediaTempIn) - TempApproachHotInlet.greaterThan!;
      }
      
      // lessThan determines MAXIMUM difference → gives UPPER BOUND for ColdMediaTempOut
      // Condition: HotMediaTempIn - ColdMediaTempOut > lessThan (difference must be greater than lessThan)
      // Which means: ColdMediaTempOut < HotMediaTempIn - lessThan
      // So this sets an UPPER BOUND (not a lower bound!)
      if (hasLessThan) {
        const upperBoundFromLessThan = parseFloat(hotMediaTempIn) - TempApproachHotInlet.lessThan!;
        if (upperBoundFromTempApproach != null) {
          upperBoundFromTempApproach = Math.min(upperBoundFromTempApproach, upperBoundFromLessThan);
        } else {
          upperBoundFromTempApproach = upperBoundFromLessThan;
        }
      }
      
      // Build suggested range message (no lower bound from tempApproach, only upper bound)
      if (upperBoundFromTempApproach != null) {
        suggestedRange = `To satisfy this validation, the ${unitConfiguration?.coldMediaType} outlet temperature should be at most ${fmtTemp(upperBoundFromTempApproach)} (assuming hot media inlet temperature remains ${fmtTemp(parseFloat(hotMediaTempIn))}).`;
      }
      
      // Calculate real acceptable range (intersection with field range)
      if (coldMediaTempOutRange) {
        let realMin: number | undefined = coldMediaTempOutRange.lessThan;
        let realMax: number | undefined = coldMediaTempOutRange.greaterThan;
        
        // Apply UPPER bound constraint (from greaterThan and/or lessThan)
        if (upperBoundFromTempApproach != null && !isNaN(upperBoundFromTempApproach)) {
          if (realMax != null) {
            realMax = Math.min(realMax, upperBoundFromTempApproach);
          } else {
            realMax = upperBoundFromTempApproach;
          }
        }
        
        if (realMin != null && realMax != null && !isNaN(realMin) && !isNaN(realMax) && realMin <= realMax) {
          const rangeConstraintText = hasLessThan && hasGreaterThan
            ? `between ${fmtDelta(TempApproachHotInlet.lessThan)} and ${fmtDelta(TempApproachHotInlet.greaterThan)}`
            : hasLessThan
            ? `at least ${fmtDelta(TempApproachHotInlet.lessThan)}`
            : `at most ${fmtDelta(TempApproachHotInlet.greaterThan)}`;
            
          fieldRangeInfo = ` Additionally, the ${unitConfiguration?.coldMediaType} outlet temperature must also satisfy its individual field validation range (${fmtTemp(coldMediaTempOutRange.lessThan)} to ${fmtTemp(coldMediaTempOutRange.greaterThan)}). The real acceptable range that satisfies both validations is ${fmtTemp(realMin)} to ${fmtTemp(realMax)}.`;
          
          errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} inlet temperature and ${unitConfiguration?.coldMediaType} outlet temperature is ${rangeDescription}. This difference must be ${rangeConstraintText}. ${suggestedRange}${fieldRangeInfo}`);
        } else {
          const rangeConstraintText = hasLessThan && hasGreaterThan
            ? `between ${fmtDelta(TempApproachHotInlet.lessThan)} and ${fmtDelta(TempApproachHotInlet.greaterThan)}`
            : hasLessThan
            ? `at least ${fmtDelta(TempApproachHotInlet.lessThan)}`
            : `at most ${fmtDelta(TempApproachHotInlet.greaterThan)}`;
            
          errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} inlet temperature and ${unitConfiguration?.coldMediaType} outlet temperature is ${rangeDescription}. This difference must be ${rangeConstraintText}. ${suggestedRange}`);
        }
      } else {
        const rangeConstraintText = hasLessThan && hasGreaterThan
          ? `between ${fmtDelta(TempApproachHotInlet.lessThan)} and ${fmtDelta(TempApproachHotInlet.greaterThan)}`
          : hasLessThan
          ? `at least ${fmtDelta(TempApproachHotInlet.lessThan)}`
          : `at most ${fmtDelta(TempApproachHotInlet.greaterThan)}`;
          
        errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} inlet temperature and ${unitConfiguration?.coldMediaType} outlet temperature is ${rangeDescription}. This difference must be ${rangeConstraintText}. ${suggestedRange}`);
      }
    }
  }
  
  // console.log('TempApproachHotOutletCurr', TempApproachHotOutletCurr);
  // console.log('TempApproachHotOutlet', TempApproachHotOutlet);
  // console.log('TempApproachHotOutlet.lessThan', TempApproachHotOutlet?.lessThan);
  // console.log('TempApproachHotOutlet.greaterThan', TempApproachHotOutlet?.greaterThan);
  // console.log('TempApproachHotOutlet.lessThan !== 0', TempApproachHotOutlet?.lessThan !== 0);
  // console.log('TempApproachHotOutlet.greaterThan !== 0', TempApproachHotOutlet?.greaterThan !== 0);
  
  // Check TempApproachHotOutlet validation
  // Validate only if at least one constraint is defined
  if (TempApproachHotOutlet && !Number.isNaN(TempApproachHotOutletCurr)) {
    const hasLessThan = TempApproachHotOutlet.lessThan != null && TempApproachHotOutlet.lessThan !== 0;
    const hasGreaterThan = TempApproachHotOutlet.greaterThan != null && TempApproachHotOutlet.greaterThan !== 0;
    const violatesLessThan = hasLessThan && roundedDisplayDelta(TempApproachHotOutletCurr) < roundedDisplayDelta(TempApproachHotOutlet.lessThan!);
    const violatesGreaterThan = hasGreaterThan && roundedDisplayDelta(TempApproachHotOutletCurr) > roundedDisplayDelta(TempApproachHotOutlet.greaterThan!);
    
    if (violatesLessThan || violatesGreaterThan) {
      // Create a more descriptive error message based on the actual validation failure
      let rangeDescription = '';
      let suggestedRange = '';
      let fieldRangeInfo = '';
      
      // Get the individual field validation range for ColdMediaTempIn
      const coldMediaTempInRange = rangeValidations?.find(x => x.propertyName === Constants.ColdMediaTempIn);
      
      if (violatesLessThan) {
        rangeDescription = `too small (${fmtDelta(TempApproachHotOutletCurr)}) - must be at least ${fmtDelta(TempApproachHotOutlet.lessThan)}`;
      } else if (violatesGreaterThan) {
        rangeDescription = `too large (${fmtDelta(TempApproachHotOutletCurr)}) - must be at most ${fmtDelta(TempApproachHotOutlet.greaterThan)}`;
      }
      
      // Calculate the suggested range for cold media inlet temperature
      // Use the already extracted value
      
      // greaterThan determines MINIMUM difference → gives UPPER BOUND for ColdMediaTempIn
      // Condition: HotMediaTempOut - ColdMediaTempIn ≥ greaterThan
      // Which means: ColdMediaTempIn ≤ HotMediaTempOut - greaterThan
      let upperBoundFromTempApproach: number | undefined = undefined;
      if (hasGreaterThan) {
        upperBoundFromTempApproach = parseFloat(hotMediaTempOut) - TempApproachHotOutlet.greaterThan!;
      }
      
      // lessThan determines MAXIMUM difference → gives UPPER BOUND for ColdMediaTempIn
      // Condition: HotMediaTempOut - ColdMediaTempIn > lessThan (difference must be greater than lessThan)
      // Which means: ColdMediaTempIn < HotMediaTempOut - lessThan
      // So this sets an UPPER BOUND (not a lower bound!)
      if (hasLessThan) {
        const upperBoundFromLessThan = parseFloat(hotMediaTempOut) - TempApproachHotOutlet.lessThan!;
        if (upperBoundFromTempApproach != null) {
          upperBoundFromTempApproach = Math.min(upperBoundFromTempApproach, upperBoundFromLessThan);
        } else {
          upperBoundFromTempApproach = upperBoundFromLessThan;
        }
      }
      
      // Build suggested range message (no lower bound from tempApproach, only upper bound)
      if (upperBoundFromTempApproach != null) {
        suggestedRange = `To satisfy this validation, the ${unitConfiguration?.coldMediaType} inlet temperature should be at most ${fmtTemp(upperBoundFromTempApproach)} (assuming hot media outlet temperature remains ${fmtTemp(parseFloat(hotMediaTempOut))}).`;
      }
      
      // Calculate real acceptable range (intersection with field range)
      if (coldMediaTempInRange) {
        let realMin: number | undefined = coldMediaTempInRange.lessThan;
        let realMax: number | undefined = coldMediaTempInRange.greaterThan;
        
        // Apply UPPER bound constraint (from greaterThan and/or lessThan)
        if (upperBoundFromTempApproach != null && !isNaN(upperBoundFromTempApproach)) {
          if (realMax != null) {
            realMax = Math.min(realMax, upperBoundFromTempApproach);
          } else {
            realMax = upperBoundFromTempApproach;
          }
        }
        
        if (realMin != null && realMax != null && !isNaN(realMin) && !isNaN(realMax) && realMin <= realMax) {
          const rangeDescriptionText = hasLessThan && hasGreaterThan
            ? `between ${fmtDelta(TempApproachHotOutlet.lessThan)} and ${fmtDelta(TempApproachHotOutlet.greaterThan)}`
            : hasLessThan
            ? `at least ${fmtDelta(TempApproachHotOutlet.lessThan)}`
            : `at most ${fmtDelta(TempApproachHotOutlet.greaterThan)}`;
            
          fieldRangeInfo = ` Additionally, the ${unitConfiguration?.coldMediaType} inlet temperature must also satisfy its individual field validation range (${fmtTemp(coldMediaTempInRange.lessThan)} to ${fmtTemp(coldMediaTempInRange.greaterThan)}). The real acceptable range that satisfies both validations is ${fmtTemp(realMin)} to ${fmtTemp(realMax)}.`;
          
          errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} outlet temperature and ${unitConfiguration?.coldMediaType} inlet temperature is ${rangeDescription}. This difference must be ${rangeDescriptionText}. ${suggestedRange}${fieldRangeInfo}`);
        } else {
          const rangeDescriptionText = hasLessThan && hasGreaterThan
            ? `between ${fmtDelta(TempApproachHotOutlet.lessThan)} and ${fmtDelta(TempApproachHotOutlet.greaterThan)}`
            : hasLessThan
            ? `at least ${fmtDelta(TempApproachHotOutlet.lessThan)}`
            : `at most ${fmtDelta(TempApproachHotOutlet.greaterThan)}`;
            
          errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} outlet temperature and ${unitConfiguration?.coldMediaType} inlet temperature is ${rangeDescription}. This difference must be ${rangeDescriptionText}. ${suggestedRange}`);
        }
      } else {
        const rangeDescriptionText = hasLessThan && hasGreaterThan
          ? `between ${fmtDelta(TempApproachHotOutlet.lessThan)} and ${fmtDelta(TempApproachHotOutlet.greaterThan)}`
          : hasLessThan
          ? `at least ${fmtDelta(TempApproachHotOutlet.lessThan)}`
          : `at most ${fmtDelta(TempApproachHotOutlet.greaterThan)}`;
          
        errorMessages.push(`Temperature approach validation failed: The difference between ${unitConfiguration?.hotMediaType} outlet temperature and ${unitConfiguration?.coldMediaType} inlet temperature is ${rangeDescription}. This difference must be ${rangeDescriptionText}. ${suggestedRange}`);
      }
    }
  }
  
  // Check DeltaTemperatureColdSide validation
  if (DeltaTemperatureColdSide && !Number.isNaN(DeltaTemperatureColdSideCurr) && (DeltaTemperatureColdSide.lessThan !== 0 && DeltaTemperatureColdSide.greaterThan !== 0) && (roundedDisplayDelta(DeltaTemperatureColdSideCurr) < roundedDisplayDelta(DeltaTemperatureColdSide.lessThan) || roundedDisplayDelta(DeltaTemperatureColdSideCurr) > roundedDisplayDelta(DeltaTemperatureColdSide.greaterThan))) {
    // Create a more descriptive error message based on the actual validation failure
    let rangeDescription = '';
    if (roundedDisplayDelta(DeltaTemperatureColdSideCurr) < roundedDisplayDelta(DeltaTemperatureColdSide.lessThan)) {
      rangeDescription = `too small (${fmtDelta(DeltaTemperatureColdSideCurr)}) - must be at least ${fmtDelta(DeltaTemperatureColdSide.lessThan)}`;
    } else if (roundedDisplayDelta(DeltaTemperatureColdSideCurr) > roundedDisplayDelta(DeltaTemperatureColdSide.greaterThan)) {
      rangeDescription = `too large (${fmtDelta(DeltaTemperatureColdSideCurr)}) - must be at most ${fmtDelta(DeltaTemperatureColdSide.greaterThan)}`;
    }
    
    errorMessages.push(`Temperature delta validation failed: The difference between ${unitConfiguration?.coldMediaType} outlet temperature and ${unitConfiguration?.coldMediaType} inlet temperature is ${rangeDescription}. This difference must be between ${fmtDelta(DeltaTemperatureColdSide.lessThan)} and ${fmtDelta(DeltaTemperatureColdSide.greaterThan)}.`);
  }
  
  // Check DeltaTemperatureHotSide validation
  if (DeltaTemperatureHotSide && !Number.isNaN(DeltaTemperatureHotSideCurr) && (DeltaTemperatureHotSide.lessThan !== 0 && DeltaTemperatureHotSide.greaterThan !== 0) && (roundedDisplayDelta(DeltaTemperatureHotSideCurr) < roundedDisplayDelta(DeltaTemperatureHotSide.lessThan) || roundedDisplayDelta(DeltaTemperatureHotSideCurr) > roundedDisplayDelta(DeltaTemperatureHotSide.greaterThan))) {
    // Create a more descriptive error message based on the actual validation failure
    let rangeDescription = '';
    if (roundedDisplayDelta(DeltaTemperatureHotSideCurr) < roundedDisplayDelta(DeltaTemperatureHotSide.lessThan)) {
      rangeDescription = `too small (${fmtDelta(DeltaTemperatureHotSideCurr)}) - must be at least ${fmtDelta(DeltaTemperatureHotSide.lessThan)}`;
    } else if (roundedDisplayDelta(DeltaTemperatureHotSideCurr) > roundedDisplayDelta(DeltaTemperatureHotSide.greaterThan)) {
      rangeDescription = `too large (${fmtDelta(DeltaTemperatureHotSideCurr)}) - must be at most ${fmtDelta(DeltaTemperatureHotSide.greaterThan)}`;
    }
    
    errorMessages.push(`Temperature delta validation failed: The difference between ${unitConfiguration?.hotMediaType} inlet temperature and ${unitConfiguration?.hotMediaType} outlet temperature is ${rangeDescription}. This difference must be between ${fmtDelta(DeltaTemperatureHotSide.lessThan)} and ${fmtDelta(DeltaTemperatureHotSide.greaterThan)}.`);
  }

  return errorMessages;
}

/**
 * Function to validate input fields and determine if form has errors
 */
export function validateInputFields(
  state: ProductDesignFormState,
  hasError: boolean = false
): boolean {
  if (hasError) {
    return true;
  }
  
  // Check if there are any errors in the form
  const hasAnyErrors = state.coldMediaTempInError !== '' ||
    state.coldMediaTempOutError !== '' ||
    state.coldMediaFlowrateError !== '' ||
    state.coldMediaPressureDropError !== '' ||
    state.hotMediaTempInError !== '' ||
    state.hotMediaTempOutError !== '' ||
    state.hotMediaFlowrateError !== '' ||
    state.hotMediaPressureDropError !== '' ||
    state.temperatureError.length > 0; // Changed from string comparison to array length check

  // Check if there are any errors in multi-section inputs
  const hasMultiSectionErrors = state.unitConfiguration?.isMultiSection && 
    state.userInputs?.some(input => input.errorMessage && input.errorMessage !== '');

  return hasAnyErrors || hasMultiSectionErrors || false;
}

/**
 * Function to check if a field is relevant for input
 */
export function isRelevantInputField(field: string, unitConfiguration: IMultiSectionUnitConfiguration | undefined): boolean {
  return unitConfiguration?.inputs?.find(x => x === field) !== undefined;
}

/**
 * Function to check if a field is relevant for output
 */
export function isRelevantOutputField(field: string, unitConfiguration: IMultiSectionUnitConfiguration | undefined): boolean {
  return unitConfiguration?.inputs?.find(x => x === field) === undefined && unitConfiguration?.nonUsedProperties?.find(x => x === field) === undefined;
}

/**
 * Function to validate if the form is completely filled
 */
export function validateFormCompletion(): boolean {
  const form = document.getElementById("designForm");
  let isFormFilled = true; // Changed to true - assume form is filled initially
  if (form) {
    form.querySelectorAll("input, textarea").forEach(function (i: any) {
      if (i.value === "") {
        isFormFilled = false; // Set to false if any field is empty
      }
    });
  }
  return isFormFilled; // Return true if all fields are filled, false if any are empty
}

/**
 * Function to check if the current process is beer pasteurizer
 */
export function isBeerPasteurizerInfo(processedQuery: string): boolean {
  return processedQuery?.toLowerCase().indexOf(Constants.BEER_PASTEURIZER) > -1;
}

/**
 * Function to check if the current process is beer cooling
 */
export function isBeerCoolingInfo(processedQuery: string): boolean {
  return processedQuery?.toLowerCase().indexOf(Constants.BEER_COOLING) > -1;
} 